<template>
    <div
        v-if="library">
        <div>
            <div class="has-margin-top">
                <div v-if="isScopedLibrary">
                    <b-field>
                        <p class="control">
                            <scope-filter-dropdown
                                :library="library"
                                scope-filter-position="is-bottom-right" />
                        </p>
                        <div class="control has-icons-left has-margin-bottom">
                            <input
                                v-model="urlSearchFilter"
                                type="text"
                                class="input"
                                placeholder="Filter by scope"
                                style="width: 60ch">
                            <span class="icon is-small is-left">
                                <i class="mdi mdi-magnify" />
                            </span>
                        </div>
                    </b-field>
                    <div v-if="selectedScopeFilter?.length">
                        <div>
                            Showing library sets for the following scopes:
                        </div>
                        <div
                            class="column is-half">
                            <tag-list
                                :tags="selectedScopeFilter"
                                :icon-class="'mdi mdi-eraser'"
                                @click-tag="componentUpdateScopeFilter" />
                        </div>
                    </div>
                </div>
            </div>

            <b-table
                :data="filteredCodeSets"
                default-sort="description"
                :striped="true"
                :narrowed="false"
                :hoverable="true"
                class="site-sticky-header">
                <b-table-column
                    v-slot="props"
                    field="description"
                    label="Description"
                    sortable>
                    <router-link :to="getCodeSetLink(props.row.name)">
                        {{ props.row.description }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    label="Create/Update item"
                    centered>
                    <span v-require-can-edit-code="{ libraryName: library.name, scopes: props.row.scopes }">
                        <b-button
                            size="is-small"
                            icon-left="pencil-plus"
                            :disabled="!props.row.scopes.length && !library.isGlobal"
                            :aria-label="`Create or update an item in ${props.row.description}`"
                            @click="openFindOrEdit(props.row)" />
                    </span>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    label="Export"
                    centered>
                    <button
                        class="button is-small"
                        :class="{'is-loading': exportLoading === props.row.name}"
                        :aria-label="`Export ${props.row.description}`"
                        @click="exportCodeSet(props.row.name, props.row.description)">
                        <i class="mdi mdi-download" />
                    </button>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="scopes"
                    label="Scopes">
                    <div class="tags">
                        <span
                            v-for="scope in props.row.scopes.toSorted()"
                            :key="scope"
                            class="tag is-small is-success">
                            {{ scope }}
                        </span>
                    </div>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="validItemCount"
                    label="Valid items"
                    numeric
                    sortable>
                    <span :title="props.row.statisticsUpdatedDate ? `Updated ${longDate(props.row.statisticsUpdatedDate)}` : ''">
                        {{ props.row.validItemCount === null ? '–' : formatNumber(props.row.validItemCount) }}
                    </span>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="invalidItemCount"
                    label="Invalid items"
                    numeric
                    sortable>
                    <span :title="props.row.statisticsUpdatedDate ? `Updated ${longDate(props.row.statisticsUpdatedDate)}` : ''">
                        {{ props.row.invalidItemCount === null ? '–' : formatNumber(props.row.invalidItemCount) }}
                    </span>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="lockedForDelete"
                    label="Delete lock"
                    title="Locked for item delete operations"
                    centered>
                    <bool-element :value="props.row.lockedForDelete" />
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="isReleaseReady"
                    label="Release ready"
                    title="Is published to external systems"
                    centered>
                    <bool-element :value="props.row.isReleaseReady" />
                </b-table-column>
            </b-table>

            <!-- Not closable with Escape: Buefy closes on any Escape in the page, including one that only closes
                 a dropdown or dialog inside the item editor, which would discard the edit.
                 The modal grows with its content, from a minimum height so it does not shrink to almost nothing
                 between views, up to the height of the window. -->
            <modal-base
                v-if="findOrEditActive && findOrEditCodeSet"
                :can-cancel="false"
                width="75rem"
                max-width="90vw"
                min-height="min(32rem, calc(100vh - 4rem))"
                max-height="calc(100vh - 4rem)">
                <template #modal-header>
                    Create or update item in {{ findOrEditCodeSet.description }}
                </template>
                <template #modal-body>
                    <code-find-or-edit
                        ref="findOrEdit"
                        :library="library.name"
                        :code-set-name="findOrEditCodeSet.name"
                        :scopes="findOrEditCodeSet.scopes"
                        @refresh="$emit('refresh')"
                        @cancel="findOrEditActive = false" />
                </template>
                <template #modal-footer>
                    <b-button
                        label="Close"
                        @click="$refs.findOrEdit.requestClose()" />
                </template>
            </modal-base>

            <div
                v-show="filteredCodeSets.length === 1"
                class="click-here-container">
                <span style="font-size:3.5em">↑</span><br><span style="font-size:2.5em">Click above</span><br><span style="font-size:1.5em">to access library content</span>
            </div>
        </div>
    </div>
</template>

<script>

    import saveAs from 'file-saver';
    import TagList from '@/shared/components/TagList.vue';
    import ScopeFilterDropdown from '@/shared/components/ScopeFilterDropdown.vue';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import CodeFindOrEdit from '@/shared/codeedit/CodeFindOrEdit.vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { requireCanEditCode } from '@/shared/directives/requirePermission';
    import { getCodeSetLink } from '@/shared/helpers/routing';
    import { excelExportCodeSet } from '@/shared/helpers/api';
    import { formatNumber, longDate } from '@/formatters';
    import { useScopeFilterStore } from '@/stores/scopeFilterStore.ts';
    import { mapActions, mapState } from 'pinia';

    export default {
        components: {
            BoolElement,
            CodeFindOrEdit,
            ModalBase,
            TagList,
            ScopeFilterDropdown
        },
        directives: {
            'require-can-edit-code': requireCanEditCode
        },
        props: {
            library: {
                type: Object,
                required: true
            },
            statistics: {
                type: Array,
                default: () => []
            }
        },
        // An item was saved through the find-or-edit modal, so the item counts may have changed
        emits: ['refresh'],
        data() {
            return {
                exportLoading: '',
                urlSearchFilter: '',
                findOrEditActive: false,
                findOrEditCodeSet: null
            };
        },
        url: {
            urlScopeFilter: {
                param: 'ScopeFilter',
                noHistory: true
            },
            urlSearchFilter: {
                param: 'SearchFilter',
                noHistory: true
            }
        },
        computed: {
            ...mapState(useScopeFilterStore, ['selectedScopeFilter']),
            urlScopeFilter: {
                get() {
                    return this.selectedScopeFilter;
                },
                async set(value) {
                    if (this.isScopedLibrary) {
                        await this.setScopeType(this.library.scopeType);
                        await this.componentUpdateScopeFilter(value);
                    }
                }
            },
            isScopedLibrary() {
                return !this.library.isGlobal;
            },
            filteredCodeSets() {
                let filtered = this.library.codeSets;

                if (this.urlSearchFilter?.length) {
                    filtered = filtered.filter(
                        cs => this.urlSearchFilter === undefined
                            || cs.description.toLowerCase().includes(this.urlSearchFilter.toLowerCase()));
                }

                if (this.isScopedLibrary && this.selectedScopeFilter?.length) {
                    filtered = filtered.filter(l => l.scopes.some(t => this.selectedScopeFilter.includes(t)));
                }

                const statsByName = new Map(this.statistics.map(s => [s.codeSetName, s]));

                return filtered.map(cs => {
                    const stats = statsByName.get(cs.name);
                    return {
                        ...cs,
                        validItemCount: stats ? stats.validCodeCount : null,
                        invalidItemCount: stats ? stats.codeCount - stats.validCodeCount : null,
                        statisticsUpdatedDate: stats ? stats.statisticsUpdatedDate : null
                    };
                });
            }
        },
        methods: {
            openFindOrEdit(codeSet) {
                this.findOrEditCodeSet = codeSet;
                this.findOrEditActive = true;
            },
            async exportCodeSet(codeSetName, codeSetDescription) {
                this.exportLoading = codeSetName;
                this.codeFilter = this.codeFilter || { textFilter: '' };
                const requestFilter = { Regex: [], Queries: [] };
                const response = await excelExportCodeSet(this, codeSetName, requestFilter);

                if (response.data) {
                    const filename = codeSetDescription ? codeSetDescription.replace(/\s/g, '-') + '.xlsx' : 'export.xlsx';
                    const blob = new Blob([response.data], { type: response.headers['Content-Type'] });
                    saveAs(blob, filename);
                }

                this.exportLoading = '';
            },
            ...mapActions(useScopeFilterStore, ['updateScopeFilter', 'setScopeType']),
            async componentUpdateScopeFilter(scope) {
                await this.updateScopeFilter(scope);
            },
            getCodeSetLink,
            formatNumber,
            longDate
        }
    };
</script>

<style scoped>
.click-here-container {
    margin-top: 10px;
    margin-left: 30px;
    color: red;
    font-weight: bold;
}
</style>
